import { useState, useEffect, useMemo, useCallback } from "react";
import {
  Form,
  Button,
  Card,
  Image,
  Spinner,
  Alert,
  Modal,
  InputGroup,
  Row,
  Col,
} from "react-bootstrap";
import {
  FaCamera,
  FaUserCircle,
  FaUser,
  FaEnvelope,
  FaMapMarkerAlt,
  FaUserTag,
  FaUsers,
  FaHourglass,
  FaCalendarWeek,
} from "react-icons/fa";
import { MdOutlineAccessTimeFilled } from "react-icons/md";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";

const ROLE_LABELS = {
  1: "Employee",
  2: "Team Leader",
  3: "HR",
  4: "Admin",
};

const WEEKLY_OFF_OPTIONS = [
  { value: "Sunday", label: "Sunday" },
  { value: "Monday", label: "Monday" },
  { value: "Tuesday", label: "Tuesday" },
  { value: "Wednesday", label: "Wednesday" },
  { value: "Thursday", label: "Thursday" },
  { value: "Friday", label: "Friday" },
  { value: "Saturday", label: "Saturday" }
];

const isValidTime12h = (time) => {
  if (!time || time === "") return true; // Allow empty
  return /^(0[1-9]|1[0-2]):[0-5][0-9]\s(AM|PM)$/.test(time);
};

const ProfileUpdate = ({ user, onClose = () => {} }) => {
  // ─── States ────────────────────────────────────────────────────────────────
  const [allUsers, setAllUsers] = useState([]);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(user.employeeId || "");
  const [formData, setFormData] = useState({
    Name: "",
    fname: "",
    lname: "",
    nickname: "",
    email: "",
    location: "",
    role: 1,
    office: "",
    designation: "",
    _file: null,
    ci_time: '',
    duration: '',
    weekly_off: 'Sunday' // Default to Sunday
  });
  const [originalData, setOriginalData] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [showConfirm, setShowConfirm] = useState(false);

  // ─── Constants ─────────────────────────────────────────────────────────────
  const isEmployee = user.role === 1;
  const canEditAll = user.role >= 2;
  const isAdmin = user.role === 4;
  const employeeIdToUse = isAdmin ? selectedEmployeeId : user.employeeId;

  // ─── Styles ────────────────────────────────────────────────────────────────
  const containerStyle = {
    minHeight: "10vh",
    backgroundColor: "#f5f7fa"
  };

  const cardHeaderStyle = {
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    borderTopLeftRadius: "0.5rem",
    borderTopRightRadius: "0.5rem",
  };

  const saveButtonStyle = {
    background: "linear-gradient(90deg, #764ba2, #667eea)",
    border: "none",
    padding: "0.75rem",
    fontSize: "1rem",
  };

  // ─── Fetch all users for admin dropdown ────────────────────────────────────
  useEffect(() => {
    if (!isAdmin) return;

    const fetchAllUsers = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/users`);
        setAllUsers(response.data);
      } catch {
        toast.error("Failed to load user list");
      }
    };

    fetchAllUsers();
  }, [isAdmin]);

  // ─── Load profile data ─────────────────────────────────────────────────────
  useEffect(() => {
    if (isAdmin && !selectedEmployeeId) {
      setLoadingProfile(false);
      return;
    }

    if (!employeeIdToUse) {
      setError("No employeeId provided");
      setLoadingProfile(false);
      return;
    }

    const fetchProfile = async () => {
      setLoadingProfile(true);
      setError("");

      try {
        const response = await axios.get(`${API_URL}/api/profile/${employeeIdToUse}`);
        const data = response.data;
        
        const initialData = {
          Name: data.Name || "",
          fname: data.fname || "",
          lname: data.lname || "",
          nickname: data.nickname || "",
          email: data.email || "",
          location: data.location || "",
          role: data.role || 1,
          office: data.office || "",
          designation: data.designation || "",
          ci_time: data.ci_time || "",
          duration: data.duration || "",
          weekly_off: data.weekly_off || "Sunday"
        };
        
        setFormData({ ...initialData, _file: null });
        setOriginalData(initialData);
        setImagePreview(
          data.image_filename ? `${API_URL}/uploads/${data.image_filename}` : null
        );
      } catch {
        setError("Failed to load profile");
      } finally {
        setLoadingProfile(false);
      }
    };

    fetchProfile();
  }, [user, selectedEmployeeId, employeeIdToUse, isAdmin]);

  // ─── Check if form is dirty ────────────────────────────────────────────────
  const isDirty = useMemo(() => {
    if (!originalData) return false;
    
    const fieldsToCheck = [
      "Name", "fname", "lname", "nickname", "email", 
      "location", "role", "office", "designation", 
      "ci_time", "duration", "weekly_off"
    ];
    
    return fieldsToCheck.some(key => formData[key] !== originalData[key]) || 
           formData._file !== null;
  }, [formData, originalData]);

  // ─── Event Handlers ────────────────────────────────────────────────────────
  const handleChange = useCallback((e) => {
    const { name, value, type, files } = e.target;
    
    if (type === "file" && files?.[0]) {
      const file = files[0];

      if (file.type !== "image/jpeg") {
        toast.error("Only .jpg files are allowed");
        e.target.value = "";
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        toast.error("Image must be under 5 MB");
        e.target.value = "";
        return;
      }
      
      setFormData(prev => ({ ...prev, _file: file }));
      
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result);
      reader.readAsDataURL(file);
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: name === "role" ? parseInt(value, 10) : value,
      }));
    }
  }, []);

  const handleSubmit = async () => {
    setShowConfirm(false);
    
    if (formData.ci_time && !isValidTime12h(formData.ci_time)) {
      toast.error('CI Time should be in format: hh:mm AM/PM');
      return;
    }

    setSaving(true);
    setError("");

    try {
      // Update profile data
      await axios.post(`${API_URL}/api/profile/${employeeIdToUse}`, {
        Name: formData.Name,
        fname: formData.fname,
        lname: formData.lname,
        nickname: formData.nickname,
        email: formData.email,
        location: formData.location,
        role: formData.role,
        office: formData.office,
        designation: formData.designation,
        ci_time: formData.ci_time,
        duration: formData.duration,
        weekly_off: formData.weekly_off
      });

      // Upload image if changed
      if (formData._file) {
        const formDataToSend = new FormData();
        formDataToSend.append("image", formData._file);
        
        await axios.post(
          `${API_URL}/api/profile/${employeeIdToUse}/image`,
          formDataToSend,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
      }

      toast.success("Profile updated successfully");
      onClose();
    } catch (err) {
      const errorMessage = err.response?.data?.error || "Update failed";
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    if (isDirty) setShowConfirm(true);
  };

  const handleEmployeeSelect = useCallback((e) => {
    setSelectedEmployeeId(e.target.value);
  }, []);

  const handleMouseEnter = (e) => {
    e.currentTarget.style.opacity = 0.85;
  };

  const handleMouseLeave = (e) => {
    e.currentTarget.style.opacity = 1;
  };

  const handleMouseEnterImage = (e) => {
    e.currentTarget.style.transform = "scale(1.1)";
  };

  const handleMouseLeaveImage = (e) => {
    e.currentTarget.style.transform = "scale(1)";
  };

  // ─── Loading & Error States ────────────────────────────────────────────────
  if (loadingProfile) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={containerStyle}>
        <Spinner animation="grow" variant="primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={containerStyle}>
        <Alert variant="danger" className="w-75 text-center">
          {error}
        </Alert>
      </div>
    );
  }

  return (
    <>
      <ToastContainer style={{ zIndex: 99999 }} />
      
      <div className="d-flex justify-content-center align-items-center px-4 py-4" style={containerStyle}>
        <Card className="shadow-lg rounded-lg" style={{ width: "100%", maxWidth: 600, border: "none" }}>
          <div className="text-white text-center py-2" style={cardHeaderStyle}>
            <h3 className="mb-0">Update Profile</h3>
          </div>

          {/* Admin User Selection Dropdown */}
          {isAdmin && (
            <Card.Body className="pt-3 pb-0">
              <Form.Group as={Row} className="align-items-center mb-3">
                <Form.Label column sm={4} className="text-end">
                  <FaUsers style={{ marginRight: "0.5rem", color: "#764ba2" }} />
                  <b>Select Employee:</b>
                </Form.Label>
                <Col sm={6}>
                  <InputGroup>
                    <InputGroup.Text style={{ background: "#764ba2", color: "#fff", border: "none" }}>
                      <FaUserTag />
                    </InputGroup.Text>
                    <Form.Select
                      value={selectedEmployeeId}
                      onChange={handleEmployeeSelect}
                      style={{ borderColor: "#764ba2", borderLeft: "none", background: "#f9f9f9" }}
                    >
                      <option value="">— pick one —</option>
                      {allUsers.map((u) => (
                        <option key={u.id} value={u.id}>
                          {u.Name} ({u.Nickname})
                        </option>
                      ))}
                    </Form.Select>
                  </InputGroup>
                </Col>
              </Form.Group>
            </Card.Body>
          )}

          <Card.Body className={isAdmin ? "pt-0" : ""}>
            {/* Profile Image */}
            <div className="text-center mb-4">
              <div style={{ position: "relative", display: "inline-block" }}>
                {imagePreview ? (
                  <Image
                    src={imagePreview}
                    roundedCircle
                    style={{
                      width: 100,
                      height: 100,
                      objectFit: "cover",
                      border: "4px solid #fff",
                      boxShadow: "0 8px 20px rgba(0,0,0,0.15)",
                    }}
                  />
                ) : (
                  <FaUserCircle size={100} color="#ccc" />
                )}
                <label
                  htmlFor="profileImage"
                  style={{
                    position: "absolute",
                    bottom: 0,
                    right: 0,
                    background: "#fff",
                    borderRadius: "50%",
                    width: 42,
                    height: 42,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    cursor: "pointer",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
                    transition: "transform 0.2s",
                  }}
                  onMouseEnter={handleMouseEnterImage}
                  onMouseLeave={handleMouseLeaveImage}
                >
                  <FaCamera size={18} color="#764ba2" />
                </label>
                <input
                  id="profileImage"
                  type="file"
                  accept="image/jpeg"
                  style={{ display: "none" }}
                  onChange={handleChange}
                  name="_file"
                />
              </div>
              <p className="mt-2 text-muted" style={{ fontSize: "0.9rem" }}>
                Please choose a .jpg image under 5 MB.
              </p>
            </div>

            {/* Profile Form */}
            <Form onSubmit={handleFormSubmit}>
              {/* Full Name + CI Time */}
              <Row className="mb-1">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label><b>Full Name</b></Form.Label>
                    <InputGroup>
                      <InputGroup.Text><FaUser /></InputGroup.Text>
                      <Form.Control
                        placeholder="Full Name"
                        name="Name"
                        value={formData.Name}
                        onChange={handleChange}
                        disabled={!isAdmin}
                      />
                    </InputGroup>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label><b>CI Time</b></Form.Label>
                    <InputGroup>
                      <InputGroup.Text><MdOutlineAccessTimeFilled /></InputGroup.Text>
                      <Form.Control
                        name="ci_time"
                        type="text"
                        placeholder="hh:mm AM/PM"
                        value={formData.ci_time}
                        onChange={handleChange}
                        disabled={!isAdmin}
                      />
                    </InputGroup>
                  </Form.Group>
                </Col>
              </Row>

              {/* Designation + Office Nick Name */}
              <Row className="mt-1">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label><b>Designation</b></Form.Label>
                    <InputGroup>
                      <InputGroup.Text><FaUserTag /></InputGroup.Text>
                      <Form.Control
                        placeholder="Designation"
                        name="designation"
                        value={formData.designation}
                        onChange={handleChange}
                        disabled={!isAdmin}
                      />
                    </InputGroup>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label><b>Office Nick Name</b></Form.Label>
                    <InputGroup>
                      <InputGroup.Text><FaMapMarkerAlt /></InputGroup.Text>
                      <Form.Control
                        placeholder="Office Nick Name"
                        name="office"
                        value={formData.office}
                        onChange={handleChange}
                        disabled={!isAdmin}
                      />
                    </InputGroup>
                  </Form.Group>
                </Col>
              </Row>

              {/* First Name + Last Name */}
              <Row className="mt-1">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label><b>First Name</b></Form.Label>
                    <InputGroup>
                      <InputGroup.Text><FaUser /></InputGroup.Text>
                      <Form.Control
                        placeholder="First Name"
                        name="fname"
                        value={formData.fname}
                        onChange={handleChange}
                        disabled={!isAdmin}
                      />
                    </InputGroup>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label><b>Last Name</b></Form.Label>
                    <InputGroup>
                      <InputGroup.Text><FaUser /></InputGroup.Text>
                      <Form.Control
                        placeholder="Last Name"
                        name="lname"
                        value={formData.lname}
                        onChange={handleChange}
                        disabled={!isAdmin}
                      />
                    </InputGroup>
                  </Form.Group>
                </Col>
              </Row>

              {/* Preferred Name + Email */}
              <Row className="mt-1">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label><b>Preferred Name</b></Form.Label>
                    <InputGroup>
                      <InputGroup.Text><FaUserTag /></InputGroup.Text>
                      <Form.Control
                        placeholder="Preferred Name"
                        name="nickname"
                        value={formData.nickname}
                        onChange={handleChange}
                        disabled={!isAdmin}
                      />
                    </InputGroup>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label><b>Email</b></Form.Label>
                    <InputGroup>
                      <InputGroup.Text><FaEnvelope /></InputGroup.Text>
                      <Form.Control
                        type="email"
                        placeholder="Email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        disabled={isEmployee && !canEditAll}
                      />
                    </InputGroup>
                  </Form.Group>
                </Col>
              </Row>

              {/* Location + Role */}
              <Row className="mt-1">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label><b>Location</b></Form.Label>
                    <InputGroup>
                      <InputGroup.Text><FaMapMarkerAlt /></InputGroup.Text>
                      <Form.Control
                        placeholder="Location"
                        name="location"
                        value={formData.location}
                        onChange={handleChange}
                        disabled={isEmployee && !canEditAll}
                      />
                    </InputGroup>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label><b>Role</b></Form.Label>
                    <InputGroup>
                      <InputGroup.Text><FaUserTag /></InputGroup.Text>
                      <Form.Select
                        name="role"
                        value={formData.role}
                        onChange={handleChange}
                        disabled={!isAdmin}
                      >
                        {Object.entries(ROLE_LABELS).map(([key, label]) => (
                          <option key={key} value={key}>{label}</option>
                        ))}
                      </Form.Select>
                    </InputGroup>
                  </Form.Group>
                </Col>
              </Row>

              {/* Duration + Weekly Off */}
              <Row className="mt-1">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label><b>Duration</b></Form.Label>
                    <InputGroup>
                      <InputGroup.Text><FaHourglass /></InputGroup.Text>
                      <Form.Control
                        placeholder="Duration"
                        name="duration"
                        value={formData.duration}
                        onChange={handleChange}
                        disabled={!isAdmin}
                      />
                    </InputGroup>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label><b>Weekly Off</b></Form.Label>
                    <InputGroup>
                      <InputGroup.Text><FaCalendarWeek /></InputGroup.Text>
                      <Form.Select
                        name="weekly_off"
                        value={formData.weekly_off}
                        onChange={handleChange}
                        disabled={!isAdmin}
                        style={{ backgroundColor: "#f9f9f9" }}
                      >
                        {WEEKLY_OFF_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </Form.Select>
                    </InputGroup>
                  </Form.Group>
                </Col>
              </Row>

              {/* Save Button */}
              <div className="d-grid mt-4">
                <Button
                  type="submit"
                  disabled={saving || !isDirty}
                  style={saveButtonStyle}
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                >
                  {saving ? (
                    <>
                      <Spinner size="sm" animation="border" /> Saving…
                    </>
                  ) : (
                    "Save Changes"
                  )}
                </Button>
              </div>
            </Form>
          </Card.Body>
        </Card>
      </div>

      {/* Confirmation Modal */}
      <Modal show={showConfirm} onHide={() => setShowConfirm(false)} centered>
        <Modal.Header>
          <Modal.Title>Confirm Save</Modal.Title>
        </Modal.Header>
        <Modal.Body>Are you sure you want to apply these changes?</Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowConfirm(false)}>
            Discard
          </Button>
          <Button variant="primary" onClick={handleSubmit}>
            Confirm
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ProfileUpdate;